import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';
import { fetchTotalFundsRaisedOptions } from '~/services/predefinedReports';

interface LocationOption {
    value: string;
    label: string;
}
interface CenterOption {
    value: number;
    label: string;
}

const TotalFundsRaisedByMedCenterAccordionItem: React.FC = () => {
    const navigate = useNavigate();
    const [locationOptions, setLocationOptions] = useState<LocationOption[]>(
        [],
    );
    const [centerOptions, setCenterOptions] = useState<CenterOption[]>([]);
    const [selectedLocation, setSelectedLocation] =
        useState<LocationOption | null>(null);
    const [selectedCenters, setSelectedCenters] = useState<CenterOption[]>([]);

    // Load locations once
    useEffect(() => {
        fetchTotalFundsRaisedOptions()
            .then((d) => setLocationOptions(d.locations))
            .catch(() => setLocationOptions([]));
    }, []);

    // Load medical centers when location changes
    useEffect(() => {
        setSelectedCenters([]);
        if (!selectedLocation) {
            setCenterOptions([]);
            return;
        }
        fetchTotalFundsRaisedOptions(selectedLocation.value)
            .then((d) =>
                setCenterOptions(
                    d.medical_centers.map((mc) => ({
                        value: mc.id,
                        label: mc.name,
                    })),
                ),
            )
            .catch(() => setCenterOptions([]));
    }, [selectedLocation]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (selectedLocation) params.set('location', selectedLocation.value);
        if (selectedCenters.length > 0) {
            params.set(
                'medical_center',
                selectedCenters.map((c) => c.value).join(','),
            );
        }
        navigate(
            `/reports/predefined/total-funds-raised-by-med-center/results?${params.toString()}`,
        );
    };

    return (
        <div className='flex h-full w-full flex-col rounded-sm border border-stroke bg-white px-7.5 py-5 shadow-default'>
            <h4 className='text-title-xsm font-bold sm:text-title-md'>
                Total Funds Raised by Medical Center
            </h4>
            <p className='mt-2 text-sm text-body'>
                Patients with assigned medical professionals in
                approved/funded/disbursable/closed campaigns. Filter by
                location, then optionally narrow to specific medical centers.
            </p>
            <form onSubmit={handleSubmit} className='mt-4 flex flex-1 flex-col'>
                <div className='flex flex-col gap-4'>
                    <div>
                        <label className='mb-1 block text-[12px] font-bold'>
                            Location
                        </label>
                        <Select
                            menuPlacement='auto'
                            options={locationOptions}
                            isClearable
                            value={selectedLocation}
                            onChange={(opt) =>
                                setSelectedLocation(
                                    opt as LocationOption | null,
                                )
                            }
                            placeholder='Located anywhere'
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: '40px',
                                }),
                            }}
                        />
                    </div>
                    <div>
                        <label className='mb-1 block text-[12px] font-bold'>
                            Medical Center
                        </label>
                        <Select
                            menuPlacement='auto'
                            options={centerOptions}
                            isMulti
                            isDisabled={!selectedLocation}
                            value={selectedCenters}
                            onChange={(opts) =>
                                setSelectedCenters([
                                    ...(opts as MultiValue<CenterOption>),
                                ])
                            }
                            placeholder={
                                selectedLocation
                                    ? 'Any medical center'
                                    : 'Select a location first'
                            }
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: '40px',
                                }),
                            }}
                        />
                    </div>
                </div>
                <div className='mt-auto pt-6'>
                    <button
                        type='submit'
                        className='rounded-lg bg-primary px-8 py-3 text-sm font-medium text-white'
                    >
                        Run report
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TotalFundsRaisedByMedCenterAccordionItem;
